/** The StarGrid API calls the current page made, newest last, for the report's Show stats panel. */

export interface ApiCallRecord {
  readonly id: number;
  readonly method: string;
  readonly path: string;
  /** Tells apart calls to one path with different bodies (two drills are not a repeat). */
  readonly bodyKey: string;
  readonly status: number | null;
  /** Epoch ms the call started, so repeated calls can be told apart by when they ran. */
  readonly startedAt: number;
  readonly durationMs: number;
  /** The API's own time and the StarRocks share of it, from the response's Server-Timing. */
  readonly serverMs?: number;
  readonly starRocksMs?: number;
  readonly starRocksQueries?: number;
  readonly cacheBypassed: boolean;
  readonly error?: string;
}

const MAX_CALLS = 300;
let calls: readonly ApiCallRecord[] = [];
let nextId = 1;
// When the current page opened. A call the previous page started but finished late is not this page's.
let pageStartedAt = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export const recordApiCall = (call: Omit<ApiCallRecord, 'id'>): void => {
  if (call.startedAt < pageStartedAt) return;
  calls = [...calls.slice(-(MAX_CALLS - 1)), { ...call, id: nextId++ }];
  notify();
};

/** Starts a new page: the stats show only calls made from here on. */
export const startApiCallPage = (): void => {
  pageStartedAt = Date.now();
  calls = [];
  notify();
};

export const subscribeApiCalls = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const apiCallsSnapshot = (): readonly ApiCallRecord[] => calls;
